import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { fetchProducts, fetchCategories } from '../services/productService';
import './Shop.css';

const ShopCollections = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    total: 0,
    totalPages: 1
  });
  const navigate = useNavigate();

  // Get category from URL params
  useEffect(() => {
    const category = searchParams.get('category');
    const page = parseInt(searchParams.get('page')) || 1;
    setSelectedCategory(category);
    setPagination(prev => ({ ...prev, page }));
  }, [searchParams]);

  // Fetch ALL categories
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const data = await fetchCategories();
        const categoriesArray = Array.isArray(data) ? data : (data?.categories || data?.data || []);
        
        // Filter out main parent categories if you only want sub-categories, 
        // or keep all. Here we filter for sub-categories only to keep the list clean.
        const allSubCategories = categoriesArray.filter(cat => 
          cat.type === 'sub' || (cat.parent_id !== null && cat.parent_id !== 0)
        );
        setCategories(allSubCategories);
      } catch (error) {
        console.error('Error loading categories:', error);
        setCategories([]);
      }
    };
    loadCategories();
  }, []);

  // Fetch ALL products (No mainCategory filter)
  useEffect(() => {
    const loadProducts = async () => {
      setLoading(true);                    // ← keep, unchanged
      try {
        const result = await fetchProducts({
          page: 1,
          limit: 500,
          category: selectedCategory
        });

        const kokProducts = (result.products || []).filter((p) => p.collection !== 'tot');

        const startIdx = (pagination.page - 1) * pagination.limit;
        const paginatedProducts = kokProducts.slice(startIdx, startIdx + pagination.limit);

        setProducts(paginatedProducts);
        setPagination(prev => ({
          ...prev,
          total: kokProducts.length,
          totalPages: Math.ceil(kokProducts.length / prev.limit) || 1
        }));
      } catch (error) {                    // ← keep, unchanged
        console.error('Error loading products:', error);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };
    loadProducts();
  }, [selectedCategory, pagination.page, pagination.limit]);

  const handleCategoryClick = (categoryId) => {
    const params = new URLSearchParams();
    if (categoryId) {
      params.set('category', categoryId);
    }
    params.set('page', '1');
    setSearchParams(params);
  };

  const handlePageChange = (newPage) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', newPage.toString());
    setSearchParams(params);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const selectedCategoryData = categories.find(c => 
    c.id?.toString() === selectedCategory?.toString() || 
    c.name === selectedCategory
  );

  if (loading) {
    return (
      <div className="shop-page">
        <div className="shop-products">
          <div className="container">
            <div className="loading-spinner">
              <div className="spinner"></div>
              <p>Loading collection...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="shop-page">
      <div className="shop-products">
        <div className="container">
          {selectedCategoryData ? (
            <>
              <h2 className="products-category-title">{selectedCategoryData.name || selectedCategoryData.sub_category}</h2>
              {selectedCategoryData.description && (
                <p className="category-description">{selectedCategoryData.description}</p>
              )}
            </>
          ) : (
            <h2 className="products-category-title">Complete Collection</h2>
          )}

          <p className="products-count">
            Showing {products.length} of {pagination.total} products
          </p>

          {products.length > 0 ? (
            <>
              <div className="products-grid">
                {products.map((product, index) => (
                  <div 
                    key={product.id} 
                    className="product-card"
                    onClick={() => navigate(`/product/${product.id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="product-image-wrapper">
                      <img 
                        src={product.image} 
                        alt={product.name} 
                        className="product-image"
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/400x500?text=No+Image';
                        }}
                      />
                      {index < 2 && <span className="product-badge">New</span>}
                      {!product.inStock && <span className="product-badge out-of-stock">Out of Stock</span>}
                      <button 
                        className="product-choose-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/product/${product.id}`);
                        }}
                      >
                        Choose options
                      </button>
                    </div>
                    <div className="product-info">
                      <h3 className="product-name">{product.name}</h3>
                      <div className="product-price">
                        <span className="price-label">Regular price</span>
                        <span className="price-value">{product.price}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="pagination">
                  <button 
                    className="pagination-btn"
                    disabled={pagination.page <= 1}
                    onClick={() => handlePageChange(pagination.page - 1)}
                  >
                    ← Previous
                  </button>
                  
                  <div className="pagination-numbers">
                    {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map(pageNum => (
                      <button
                        key={pageNum}
                        className={`pagination-num ${pagination.page === pageNum ? 'active' : ''}`}
                        onClick={() => handlePageChange(pageNum)}
                      >
                        {pageNum}
                      </button>
                    ))}
                  </div>

                  <button 
                    className="pagination-btn"
                    disabled={pagination.page >= pagination.totalPages}
                    onClick={() => handlePageChange(pagination.page + 1)}
                  >
                    Next →
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="no-products">
              <p>No products found in this category.</p>
              <button 
                className="btn-view-all"
                onClick={() => handleCategoryClick(null)}
              >
                View All Products
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShopCollections;